import { Flex, Text, FormLabel } from '@chakra-ui/react';
import { SegmentedControl } from '../../ui';
import {
    InventoryBin,
    InventoryCategory,
    InventoryFormReferenceField,
    InventoryPartType,
    InventoryTag,
    InventoryUnit,
} from '../../types/inventory';
import { formatPartNumber } from '../../functions/formatPartNumber';

export interface InventoryItemFormState {
    manufacturerPartNumber: string;
    name: string;
    category: InventoryCategory;
    partType: InventoryPartType;
    supplierId: string;
    unit: InventoryUnit;
    restockAlert: boolean;
    reorderQuantity: number | null;
    targetQuantity: number | null;
    inStockQuantity: number;
    unitCost: number;
    locationId: string;
    partTerminologyId: number | null;
    origin: string;
    businessLine: string;
}

export const emptyInventoryItemForm = (
    locationId = '',
    overrides: Partial<InventoryItemFormState> = {},
): InventoryItemFormState => ({
    manufacturerPartNumber: '',
    name: '',
    category: 'part',
    partType: 'oem',
    supplierId: '',
    unit: 'each',
    restockAlert: false,
    reorderQuantity: null,
    targetQuantity: null,
    inStockQuantity: 0,
    unitCost: 0,
    locationId,
    partTerminologyId: null,
    origin: '',
    businessLine: '',
    ...overrides,
});

export type InventoryItemFormErrors = Partial<Record<InventoryFormReferenceField, string>>;

export interface InventoryItemFormExtras {
    tags: Array<InventoryTag>;
    bins: Array<InventoryBin>;
    imagePreviewUrl: string | null;
    imageKey: string | undefined;
    photoAndTagsOpen: boolean;
    detailsOpen: boolean;
    supplierSearch: string;
    locationSearch: string;
}

export const emptyInventoryItemFormExtras = (): InventoryItemFormExtras => ({
    tags: [],
    bins: [],
    imagePreviewUrl: null,
    imageKey: undefined,
    photoAndTagsOpen: false,
    detailsOpen: false,
    supplierSearch: '',
    locationSearch: '',
});

export const SectionHead = ({
    title,
    hint,
}: {
    title: string;
    hint?: string;
}) => (
    <Flex align="baseline" gap={2} mb={3} flexWrap="wrap">
        <Text
            fontSize="sm"
            fontWeight="bold"
            letterSpacing="0.06em"
            textTransform="uppercase"
            color="fg.default"
        >
            {title}
        </Text>
        {hint && (
            <Text fontSize="xs" color="gray.400" fontWeight="medium">
                ({hint})
            </Text>
        )}
    </Flex>
);

export const FieldLabel = ({ children }: { children: React.ReactNode }) => (
    <FormLabel
        fontSize="sm"
        fontWeight="semibold"
        color="fg.muted"
        mb={1.5}
        requiredIndicator={
            <Text as="span" color="warning.fg" ml={0.5} aria-hidden>
                *
            </Text>
        }
    >
        {children}
    </FormLabel>
);

export const RestockPillTabs = ({
    value,
    onChange,
}: {
    value: boolean;
    onChange: (on: boolean) => void;
}) => (
    <SegmentedControl
        size="sm"
        fullWidth
        items={[
            { value: 'on', label: 'On' },
            { value: 'off', label: 'Off' },
        ]}
        value={value ? 'on' : 'off'}
        onChange={(next) => onChange(next === 'on')}
    />
);

export const countMissingInventoryItemFields = (
    form: InventoryItemFormState,
    options?: { maxInStockQuantity?: number },
): number => {
    let missing = 0;
    if (!form.category) missing += 1;
    if (!formatPartNumber(form.manufacturerPartNumber)) missing += 1;
    if (!form.name.trim()) missing += 1;
    if (!form.partType) missing += 1;
    if (!form.supplierId) missing += 1;
    if (!form.locationId) missing += 1;
    if (form.inStockQuantity == null || Number.isNaN(form.inStockQuantity)) missing += 1;
    if (options?.maxInStockQuantity != null) {
        if (form.inStockQuantity < 1 || form.inStockQuantity > options.maxInStockQuantity) {
            missing += 1;
        }
    }
    if (!form.unit) missing += 1;
    if (form.restockAlert) {
        if (form.reorderQuantity == null) missing += 1;
        if (form.targetQuantity == null) missing += 1;
    }
    return missing;
};
